import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { TagLabel } from "@vd/ui";
import { FileSpreadsheet } from "lucide-react";
import { useMemo, useState } from "react";
import { z } from "zod";
import {
  type ImportBatch,
  type ImportRow,
  ik,
  importQuery,
  importsApi,
  importsQuery,
  reachedCrm,
  statusOf,
  tagChoicePath,
} from "../../../api/imports";
import { requireAdmin } from "../../../auth/useMe";
import { DataTable } from "../../../components/DataTable";
import { toast } from "../../../components/toast";
import {
  Badge,
  Button,
  Card,
  Dialog,
  EmptyState,
  ErrorState,
  Loading,
  PageHeader,
  Tabs,
} from "../../../components/ui";
import { fmtDate, fmtDateTime, fmtPhone } from "../../../lib/format";
import { useNavigate } from "../../../lib/navigate";

const searchSchema = z.object({
  id: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/admin/ai/imports")({
  staticData: { crumb: { label: "Imports", parents: [] } },
  validateSearch: (s) => searchSchema.parse(s),
  beforeLoad: requireAdmin,
  loader: ({ context }) => context.queryClient.prefetchQuery(importsQuery),
  component: ImportsPage,
});

const col = createColumnHelper<ImportBatch>();

function resultLine(b: ImportBatch): string {
  const c = b.counts;
  if (b.status === "rolled_back") {
    return c.kept ? `${c.rolled_back} removed, ${c.kept} kept` : `${c.rolled_back} removed`;
  }
  if (b.committed_at) {
    const parts = [`${c.created} added`, `${c.linked} tagged`];
    if (c.failed) parts.push(`${c.failed} failed`);
    return parts.join(", ");
  }
  return `${c.rows.toLocaleString()} rows`;
}

const columns = [
  col.accessor("code", {
    header: "Import",
    cell: (c) => <span className="tnum font-medium text-ink">{c.getValue()}</span>,
  }),
  col.accessor((b) => b.file_name ?? "", {
    id: "file",
    header: "File",
    cell: (c) => <span className="block max-w-56 truncate">{c.getValue()}</span>,
  }),
  col.accessor((b) => b.user_name ?? "", { id: "who", header: "Imported by" }),
  col.accessor("campaign_path", { header: "Campaign" }),
  col.display({
    id: "status",
    header: "Status",
    cell: (c) => {
      const s = statusOf(c.row.original.status);
      return <Badge tone={s.tone}>{s.label}</Badge>;
    },
  }),
  col.display({
    id: "result",
    header: "Result",
    cell: (c) => <span className="tnum text-ink-2">{resultLine(c.row.original)}</span>,
  }),
  col.accessor("created_at", {
    header: "Started",
    cell: (c) => <span className="tnum whitespace-nowrap">{fmtDate(c.getValue())}</span>,
  }),
];

function ImportsPage() {
  const { id } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const q = useQuery(importsQuery);
  const open = (next: string | undefined) => navigate({ search: { id: next }, replace: true });
  return (
    <>
      <PageHeader title="Imports" />
      <Card>
        {q.isPending ? (
          <Loading />
        ) : q.isError ? (
          <ErrorState error={q.error} onRetry={() => q.refetch()} />
        ) : (
          <DataTable
            data={q.data.items}
            columns={columns}
            getRowId={(b) => b.id}
            rowLabel={(b) => b.code}
            onRowClick={(b) => open(b.id)}
            empty={
              <EmptyState
                icon={<FileSpreadsheet />}
                title="No imports yet"
                description="Files reps import from the Assistant show up here."
              />
            }
          />
        )}
      </Card>
      {id ? <ImportDialog id={id} onClose={() => open(undefined)} /> : null}
    </>
  );
}

type RowFilter = "all" | "create" | "link" | "skip" | "failed" | "kept";

const ROW_TABS: { value: RowFilter; label: string }[] = [
  { value: "all", label: "All rows" },
  { value: "create", label: "New" },
  { value: "link", label: "Existing" },
  { value: "skip", label: "Skipped" },
  { value: "failed", label: "Failed" },
  { value: "kept", label: "Kept" },
];

const DECISION: Record<ImportRow["decision"], string> = {
  create: "New",
  link: "Existing",
  review: "Needs a look",
  skip: "Skipped",
};

const ROW_STATUS: Record<ImportRow["status"], string> = {
  pending: "",
  done: "Done",
  failed: "Failed",
  rolled_back: "Removed",
  kept: "Kept",
};

function ImportDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [rows, setRows] = useState<RowFilter>("all");
  const [confirming, setConfirming] = useState(false);
  const q = useQuery(importQuery(id, rows, 1));
  const b = q.data?.batch;

  const rollback = useMutation({
    mutationFn: () => importsApi.rollback(id),
    onSuccess: (done) => {
      setConfirming(false);
      toast(`Rolling back ${done.code}`, "info");
      void qc.invalidateQueries({ queryKey: ik.list });
      void qc.invalidateQueries({ queryKey: ["admin", "import", id] });
    },
    onError: (e) => toast((e as Error).message, "danger"),
  });

  const download = useMutation({
    mutationFn: () => importsApi.file(id),
    onSuccess: (f) => window.open(f.url, "_blank", "noopener"),
    onError: (e) => toast((e as Error).message, "danger"),
  });

  const rowColumns = useMemo(() => {
    const rc = createColumnHelper<ImportRow>();
    return [
      rc.accessor("row_no", {
        header: "Row",
        cell: (c) => <span className="tnum text-ink-3">{c.row.original.source_row ?? c.getValue()}</span>,
      }),
      rc.accessor((r) => r.normalized.name ?? r.normalized.company ?? "", {
        id: "name",
        header: "Name",
        cell: (c) => (
          <span className="flex flex-col">
            <span className="text-ink">{c.getValue()}</span>
            {c.row.original.normalized.company && c.row.original.normalized.name ? (
              <span className="text-caption text-ink-3">{c.row.original.normalized.company}</span>
            ) : null}
          </span>
        ),
      }),
      rc.accessor((r) => (r.normalized.phone ? fmtPhone(r.normalized.phone) : (r.normalized.email ?? "")), {
        id: "contact",
        header: "Phone or email",
        cell: (c) => <span className="tnum text-ink-2">{c.getValue()}</span>,
      }),
      rc.accessor("decision", {
        header: "Decision",
        cell: (c) => {
          const r = c.row.original;
          return (
            <span className="flex flex-col">
              <span>{DECISION[r.decision]}</span>
              {r.match?.name ? <span className="text-caption text-ink-3">{r.match.name}</span> : null}
              {r.skip_reason ? <span className="text-caption text-ink-3">{r.skip_reason}</span> : null}
            </span>
          );
        },
      }),
      rc.accessor("status", {
        header: "Result",
        cell: (c) => {
          const r = c.row.original;
          return (
            <span className="flex flex-col">
              <span className={r.status === "failed" ? "text-danger-ink" : ""}>{ROW_STATUS[r.status]}</span>
              {r.error ? <span className="text-caption text-ink-3">{r.error}</span> : null}
              {r.crm_ref ? <span className="tnum text-caption text-ink-3">{r.crm_ref}</span> : null}
            </span>
          );
        },
      }),
    ];
  }, []);

  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title={b?.code ?? "Import"}
      {...(b?.file_name ? { description: b.file_name } : {})}
      footer={
        b ? (
          <>
            {b.file_name ? (
              <Button onClick={() => download.mutate()} busy={download.isPending}>
                Download file
              </Button>
            ) : null}
            {reachedCrm(b) ? (
              <Button variant="danger" onClick={() => setConfirming(true)}>
                Roll back
              </Button>
            ) : null}
          </>
        ) : undefined
      }
    >
      {q.isPending ? (
        <Loading />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : b ? (
        <div className="flex flex-col gap-4">
          <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-1.5 text-body">
            <dt className="text-ink-3">Status</dt>
            <dd>
              <Badge tone={statusOf(b.status).tone}>{statusOf(b.status).label}</Badge>
            </dd>
            <dt className="text-ink-3">Imported by</dt>
            <dd>{b.user_name ?? ""}</dd>
            <dt className="text-ink-3">Campaign</dt>
            <dd>{b.campaign_path}</dd>
            {b.purpose ? (
              <>
                <dt className="text-ink-3">Purpose</dt>
                <dd className="text-ink-2">{b.purpose}</dd>
              </>
            ) : null}
            <dt className="text-ink-3">Tags</dt>
            <dd className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
              {[...b.system_tags, ...b.user_tags.map(tagChoicePath)].map((t) => (
                <TagLabel key={t} path={t} />
              ))}
            </dd>
            <dt className="text-ink-3">Result</dt>
            <dd className="tnum">{resultLine(b)}</dd>
            {b.committed_at ? (
              <>
                <dt className="text-ink-3">Imported</dt>
                <dd className="tnum">{fmtDateTime(b.committed_at)}</dd>
              </>
            ) : null}
            {b.rolled_back_at ? (
              <>
                <dt className="text-ink-3">Rolled back</dt>
                <dd className="tnum">{fmtDateTime(b.rolled_back_at)}</dd>
              </>
            ) : null}
            {b.error ? (
              <>
                <dt className="text-ink-3">Error</dt>
                <dd className="text-danger-ink">{b.error}</dd>
              </>
            ) : null}
          </dl>
          <div>
            <Tabs value={rows} tabs={ROW_TABS} onChange={setRows} label="Rows" />
            <DataTable
              data={q.data.rows}
              columns={rowColumns}
              getRowId={(r) => String(r.row_no)}
              empty={<EmptyState title="No rows here" />}
            />
            {q.data.total > q.data.rows.length ? (
              <p className="pt-2 text-caption text-ink-3">
                Showing {q.data.rows.length} of {q.data.total.toLocaleString()}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
      {confirming && b ? (
        <Dialog
          open
          tone="danger"
          size="sm"
          onClose={() => setConfirming(false)}
          title={`Roll back ${b.code}?`}
          description="Contacts it added are archived, and the tags it added are removed. Contacts someone changed since are kept."
          footer={
            <>
              <Button onClick={() => setConfirming(false)}>Cancel</Button>
              <Button variant="danger" busy={rollback.isPending} onClick={() => rollback.mutate()}>
                Roll back
              </Button>
            </>
          }
        />
      ) : null}
    </Dialog>
  );
}
